import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

function ConsultaCpf() {
  const [tipoConsulta, setTipoConsulta] = useState('cpf');
  const [valor, setValor] = useState('');
  const [timeout, setTimeout] = useState(10);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [limiteInfo, setLimiteInfo] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [temPermissao, setTemPermissao] = useState(false);
  const [filaInfo, setFilaInfo] = useState(null);
  const [posicaoFila, setPosicaoFila] = useState(null);
  const [tempoRestante, setTempoRestante] = useState(null);

  useEffect(() => {
    loadUserAndPermissions();
    loadLimiteInfo();
  }, []);

  const loadLimiteInfo = async () => {
    try {
      const response = await axios.get('/consultas-cpf/stats');
      setLimiteInfo(response.data);
    } catch (err) {
      console.error('Erro ao carregar informações de limite:', err);
    }
  };

  const loadUserAndPermissions = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUser(user);

        // Verificar permissão de CPF
        try {
          // Buscar dados atualizados do usuário
          const response = await axios.get('/users/me');
          const currentUserData = response.data;
          
          if (currentUserData) {
            setCurrentUser(currentUserData);
            localStorage.setItem('user', JSON.stringify(currentUserData));
            
            // Verificar permissão de CPF
            setTemPermissao(currentUserData.pode_consultar_cpf === true || currentUserData.role === 'admin');
          } else {
            // Fallback: admin sempre tem permissão
            setTemPermissao(user.role === 'admin');
          }
        } catch (err) {
          console.error('Erro ao verificar permissão:', err);
          // Fallback: admin sempre tem permissão
          setTemPermissao(user.role === 'admin');
        }
      }
    } catch (err) {
      console.error('Erro ao carregar usuário:', err);
    }
  };

  // Função para normalizar valor baseado no tipo
  const normalizarValor = (tipo, valorInput) => {
    if (!valorInput) return '';
    
    if (tipo === 'cpf' || tipo === 'cpf2' || tipo === 'telefone' || tipo === 'telefone2') {
      // Para CPF e telefone: remover caracteres não numéricos
      return String(valorInput).replace(/\D/g, '');
    } else if (tipo === 'nome' || tipo === 'nome2') {
      // Para nome: trim e normalizar espaços
      return String(valorInput).trim().replace(/\s+/g, ' ');
    }
    return String(valorInput).trim();
  };

  // Função para validar valor baseado no tipo
  const validarValor = (tipo, valorNormalizado) => {
    if (!valorNormalizado) return false;
    
    if (tipo === 'cpf' || tipo === 'cpf2') {
      // CPF: exatamente 11 dígitos
      return valorNormalizado.length === 11 && /^\d{11}$/.test(valorNormalizado);
    } else if (tipo === 'telefone' || tipo === 'telefone2') {
      // Telefone: 10 ou 11 dígitos
      return valorNormalizado.length >= 10 && valorNormalizado.length <= 11 && /^\d{10,11}$/.test(valorNormalizado);
    } else if (tipo === 'nome' || tipo === 'nome2') {
      // Nome: pelo menos 2 palavras (nome e sobrenome)
      const palavras = valorNormalizado.split(/\s+/).filter(p => p.length > 0);
      return palavras.length >= 2;
    }
    return false;
  };

  // Handler para mudança no input
  const handleValorChange = (e) => {
    const valorInput = e.target.value;
    
    // Para CPF e telefone, normalizar durante a digitação (remover caracteres não numéricos)
    if (tipoConsulta === 'cpf' || tipoConsulta === 'cpf2') {
      const valorNormalizado = String(valorInput).replace(/\D/g, '');
      if (valorNormalizado.length <= 11) {
        setValor(valorNormalizado);
        setError('');
      }
    } else if (tipoConsulta === 'telefone' || tipoConsulta === 'telefone2') {
      const valorNormalizado = String(valorInput).replace(/\D/g, '');
      if (valorNormalizado.length <= 11) {
        setValor(valorNormalizado);
        setError('');
      }
    } else {
      // Para nome, permitir digitação livre (não normalizar durante a digitação)
      setValor(valorInput);
      setError('');
    }
  };

  // Handler para mudança no tipo de consulta
  const handleTipoConsultaChange = (e) => {
    const novoTipo = e.target.value;
    setTipoConsulta(novoTipo);
    setValor('');
    setError('');
    setResultado(null);
  };

  // Carregar informações da fila periodicamente
  useEffect(() => {
    if (loading && posicaoFila !== null) {
      const interval = setInterval(async () => {
        try {
          const response = await axios.get('/consultas-cpf/fila-info');
          setFilaInfo(response.data);
        } catch (err) {
          console.error('Erro ao atualizar informações da fila:', err);
        }
      }, 2000); // Atualizar a cada 2 segundos

      return () => clearInterval(interval);
    }
  }, [loading, posicaoFila]);

  // Contador de timeout regressivo
  useEffect(() => {
    if (loading && timeout) {
      // Iniciar com o timeout selecionado + margem de segurança (5 segundos)
      const tempoTotal = timeout + 5;
      setTempoRestante(tempoTotal);

      const interval = setInterval(() => {
        setTempoRestante((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000); // Atualizar a cada segundo

      return () => {
        clearInterval(interval);
        setTempoRestante(null);
      };
    } else {
      setTempoRestante(null);
    }
  }, [loading, timeout]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResultado(null);
    setLoading(true);
    setPosicaoFila(null);
    setFilaInfo(null);

    const valorNormalizado = normalizarValor(tipoConsulta, valor);

    if (!valorNormalizado) {
      const mensagens = {
        cpf: 'Por favor, digite um CPF',
        cpf2: 'Por favor, digite um CPF2',
        telefone: 'Por favor, digite um telefone',
        telefone2: 'Por favor, digite um telefone2',
        nome: 'Por favor, digite um nome',
        nome2: 'Por favor, digite um nome2'
      };
      setError(mensagens[tipoConsulta] || 'Por favor, digite um valor');
      setLoading(false);
      return;
    }

    if (!validarValor(tipoConsulta, valorNormalizado)) {
      const mensagens = {
        cpf: 'CPF inválido. Deve conter exatamente 11 dígitos numéricos',
        cpf2: 'CPF2 inválido. Deve conter exatamente 11 dígitos numéricos',
        telefone: 'Telefone inválido. Deve conter 10 ou 11 dígitos numéricos',
        telefone2: 'Telefone2 inválido. Deve conter 10 ou 11 dígitos numéricos',
        nome: 'Nome inválido. Deve conter pelo menos nome e sobrenome',
        nome2: 'Nome2 inválido. Deve conter pelo menos nome e sobrenome'
      };
      setError(mensagens[tipoConsulta] || 'Valor inválido');
      setLoading(false);
      return;
    }

    try {
      // Usar a rota /processar que aguarda o resultado
      const response = await axios.post('/consultas-cpf/processar', {
        tipoConsulta: tipoConsulta,
        valor: valorNormalizado,
        timeout: timeout
      });

      if (response.data.success) {
        setResultado(response.data.resultado);
        // Atualizar informações de limite
        if (response.data.limite !== undefined) {
          setLimiteInfo({
            limite: response.data.limite,
            usadas: response.data.usadas,
            restantes: response.data.restantes
          });
        }
      } else {
        setError(response.data.error || 'Erro ao realizar consulta');
      }
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Erro ao realizar consulta. Tente novamente mais tarde.');
      }
      console.error('Erro ao realizar consulta:', err);
    } finally {
      setLoading(false);
      setPosicaoFila(null);
      setFilaInfo(null);
    }
  };

  // Função para formatar o resultado (preservar quebras de linha e emojis)
  const formatarResultado = (texto) => {
    if (!texto) return '';
    // Preservar quebras de linha e emojis
    return texto.split('\n').map((linha, index) => (
      <React.Fragment key={index}>
        {linha}
        {index < texto.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  if (!temPermissao && currentUser?.role !== 'admin') {
    return (
      <div>
        <nav className="nav">
          <Link to="/dashboard" className="nav-link">
            Dashboard
          </Link>
          <Link to="/consultas" className="nav-link">
            Consultas
          </Link>
          <Link to="/consulta-cpf" className="nav-link active">
            Consulta CPF
          </Link>
          {(currentUser?.role === 'admin' || currentUser?.role === 'proprietario') && (
            <Link to="/admin" className="nav-link">
              Administração
            </Link>
          )}
          {(currentUser?.role === 'admin' || currentUser?.role === 'proprietario') && (
            <Link to="/limites-consultas" className="nav-link">
              Limites
            </Link>
          )}
        </nav>

        <div className="card">
          <h2>Consulta</h2>
          <div className="alert alert-error">
            <strong>⚠️ Acesso Negado</strong>
            <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
              Você não tem permissão para realizar consultas. Entre em contato com o administrador para solicitar acesso.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <nav className="nav">
        <Link to="/dashboard" className="nav-link">
          Dashboard
        </Link>
        <Link to="/consultas" className="nav-link">
          Consultas
        </Link>
        <Link to="/consulta-cpf" className="nav-link active">
          Consulta CPF
        </Link>
        {(currentUser?.role === 'admin' || currentUser?.role === 'proprietario') && (
          <Link to="/admin" className="nav-link">
            Administração
          </Link>
        )}
        {(currentUser?.role === 'admin' || currentUser?.role === 'proprietario') && (
          <Link to="/limites-consultas" className="nav-link">
            Limites
          </Link>
        )}
      </nav>

      <div className="card">
        <h2>Consulta</h2>
        <p style={{ color: '#999', marginBottom: '2rem' }}>
          Selecione o tipo de consulta e digite o valor correspondente
        </p>

        {/* Informações de Limite */}
        {limiteInfo && (
          <div style={{ 
            marginBottom: '2rem', 
            padding: '1rem', 
            backgroundColor: '#1a1a1a', 
            borderRadius: '4px',
            border: '1px solid #333'
          }}>
            <h3 style={{ color: '#cc0000', marginBottom: '0.5rem', fontSize: '1rem' }}>Limite de Consultas de CPF</h3>
            {limiteInfo.limite === null ? (
              <p style={{ color: '#00cc66', margin: 0 }}>Ilimitado</p>
            ) : (
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <span style={{ color: '#ccc' }}>
                  Limite: <strong style={{ color: '#cc0000' }}>{limiteInfo.limite}</strong>
                </span>
                <span style={{ color: '#ccc' }}>
                  Usadas: <strong style={{ color: '#ffaa00' }}>{limiteInfo.usadas || 0}</strong>
                </span>
                <span style={{ 
                  color: limiteInfo.restantes === 0 ? '#ff0000' : 
                          (limiteInfo.restantes <= limiteInfo.limite * 0.1 ? '#ff6600' : '#00cc66')
                }}>
                  Restantes: <strong>{limiteInfo.restantes !== null ? limiteInfo.restantes : 'Ilimitado'}</strong>
                </span>
              </div>
            )}
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="tipoConsulta">Tipo de Consulta</label>
            <select
              id="tipoConsulta"
              value={tipoConsulta}
              onChange={handleTipoConsultaChange}
              disabled={loading}
              style={{ 
                padding: '0.75rem',
                fontSize: '1rem',
                backgroundColor: '#1a1a1a',
                color: '#ccc',
                border: '1px solid #333',
                borderRadius: '4px',
                width: '100%'
              }}
            >
              <option value="cpf">CPF</option>
              <option value="cpf2">CPF2</option>
              <option value="nome">Nome</option>
              <option value="nome2">Nome2</option>
              <option value="telefone">Telefone</option>
              <option value="telefone2">Telefone2</option>
            </select>
            <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
              Selecione o tipo de consulta que deseja realizar
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="valor">
              {(tipoConsulta === 'cpf' || tipoConsulta === 'cpf2') && 'CPF (apenas números)'}
              {(tipoConsulta === 'telefone' || tipoConsulta === 'telefone2') && 'Telefone (apenas números)'}
              {(tipoConsulta === 'nome' || tipoConsulta === 'nome2') && 'Nome Completo'}
            </label>
            <input
              type="text"
              id="valor"
              value={valor}
              onChange={handleValorChange}
              placeholder={
                (tipoConsulta === 'cpf' || tipoConsulta === 'cpf2') ? 'Digite o CPF (11 dígitos)' :
                (tipoConsulta === 'telefone' || tipoConsulta === 'telefone2') ? 'Digite o telefone (10 ou 11 dígitos)' :
                'Digite o nome completo (nome e sobrenome)'
              }
              maxLength={(tipoConsulta === 'cpf' || tipoConsulta === 'cpf2') ? 11 : (tipoConsulta === 'telefone' || tipoConsulta === 'telefone2') ? 11 : undefined}
              pattern={(tipoConsulta === 'cpf' || tipoConsulta === 'cpf2' || tipoConsulta === 'telefone' || tipoConsulta === 'telefone2') ? '[0-9]*' : undefined}
              inputMode={(tipoConsulta === 'cpf' || tipoConsulta === 'cpf2' || tipoConsulta === 'telefone' || tipoConsulta === 'telefone2') ? 'numeric' : 'text'}
              required
              disabled={loading}
              style={{ 
                fontFamily: (tipoConsulta === 'cpf' || tipoConsulta === 'cpf2' || tipoConsulta === 'telefone' || tipoConsulta === 'telefone2') ? 'monospace' : 'inherit',
                fontSize: '1.1rem',
                letterSpacing: (tipoConsulta === 'cpf' || tipoConsulta === 'cpf2' || tipoConsulta === 'telefone' || tipoConsulta === 'telefone2') ? '0.1rem' : 'normal'
              }}
            />
            <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
              {(tipoConsulta === 'cpf' || tipoConsulta === 'cpf2') && 'Digite apenas números, sem pontos ou traços. O CPF será enviado automaticamente no formato correto.'}
              {(tipoConsulta === 'telefone' || tipoConsulta === 'telefone2') && 'Digite apenas números, sem parênteses, traços ou espaços. O telefone deve ter 10 ou 11 dígitos.'}
              {(tipoConsulta === 'nome' || tipoConsulta === 'nome2') && 'Digite o nome completo com pelo menos nome e sobrenome.'}
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="timeout">Timeout da API (segundos)</label>
            <select
              id="timeout"
              value={timeout}
              onChange={(e) => setTimeout(parseInt(e.target.value))}
              disabled={loading}
              style={{ 
                padding: '0.75rem',
                fontSize: '1rem',
                backgroundColor: '#1a1a1a',
                color: '#ccc',
                border: '1px solid #333',
                borderRadius: '4px',
                width: '100%'
              }}
            >
              <option value={10}>10 segundos (padrão)</option>
              <option value={30}>30 segundos</option>
              <option value={45}>45 segundos</option>
              <option value={60}>60 segundos</option>
            </select>
            <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
              Tempo máximo de espera pela resposta da API. Consultas mais complexas podem precisar de mais tempo.
            </p>
          </div>

          <button 
            type="submit" 
            style={{ backgroundColor: '#cc0000', color: '#fff', border: 'none' }}
            className="btn"
            disabled={loading || !valor || !validarValor(tipoConsulta, normalizarValor(tipoConsulta, valor))}
          >
            {loading ? 'Consultando...' : 'Consultar'}
          </button>
        </form>

        {/* Contador de Timeout */}
        {loading && tempoRestante !== null && (
          <div style={{ 
            marginTop: '1.5rem', 
            padding: '1rem', 
            backgroundColor: '#1a1a1a', 
            borderRadius: '4px',
            border: '1px solid #333',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: '2rem', 
              fontWeight: 'bold',
              color: tempoRestante <= 10 ? '#ff0000' : tempoRestante <= 20 ? '#ff6600' : '#00cc66',
              marginBottom: '0.5rem'
            }}>
              ⏱️ {tempoRestante}s
            </div>
            <p style={{ fontSize: '0.85rem', color: '#999', margin: 0 }}>
              Tempo restante para resposta da API (timeout: {timeout}s)
            </p>
          </div>
        )}

        {/* Informações da Fila */}
        {loading && filaInfo && (
          <div style={{ 
            marginTop: '1.5rem', 
            padding: '1rem', 
            backgroundColor: '#1a1a1a', 
            borderRadius: '4px',
            border: '1px solid #333'
          }}>
            <h4 style={{ color: '#cc0000', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Status da Fila</h4>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {filaInfo.processando ? (
                <span style={{ color: '#00cc66' }}>
                  🔄 Processando consulta...
                </span>
              ) : (
                <span style={{ color: '#ffaa00' }}>
                  ⏳ Aguardando na fila...
                </span>
              )}
              {filaInfo.tamanho > 0 && (
                <span style={{ color: '#ccc', fontSize: '0.9rem' }}>
                  Consultas na fila: <strong>{filaInfo.tamanho}</strong>
                </span>
              )}
            </div>
            <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem', marginBottom: 0 }}>
              As consultas são processadas uma por vez para evitar sobrecarga na API.
            </p>
          </div>
        )}

        {loading && (
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <div className="loading">Aguardando resposta da API...</div>
          </div>
        )}

        {resultado && !loading && (
          <div style={{ 
            marginTop: '2rem', 
            padding: '1.5rem', 
            backgroundColor: '#0a0a0a', 
            borderRadius: '4px',
            border: '1px solid #333'
          }}>
            <h3 style={{ color: '#cc0000', marginBottom: '1rem' }}>Resultado da Consulta</h3>
            <div style={{ 
              color: '#ccc', 
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              lineHeight: '1.6',
              maxHeight: '600px',
              overflowY: 'auto',
              padding: '1rem',
              backgroundColor: '#000',
              borderRadius: '4px',
              border: '1px solid #333'
            }}>
              {formatarResultado(resultado)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConsultaCpf;
